import React from 'react';
import {
  ColorPickerField,
  EditTabField,
  ImageUploaderField,
  InputWithUnitField,
  RadioGroupField,
  SelectField,
  TextField,
} from '@extensions/components/Form';
import { IconLink } from '@arco-design/web-react/icon';
import { Collapse, Grid, Space } from '@arco-design/web-react';
import { Stack, useEditorProps, useFocusIdx } from '@bay-email/editor';
import { AttributesPanelWrapper } from '@extensions/AttributePanel/components/attributes/AttributesPanelWrapper';
import { Align } from '@extensions/AttributePanel/components/attributes/Align';
import { ICarousel } from '@bay-email/core';
import { ClassName } from '../../attributes/ClassName';
import { CollapseWrapper } from '../../attributes/CollapseWrapper';

const options = [
  {
    value: 'hidden',
    get label() {
      return t('Hidden');
    },
  },
  {
    value: 'visible',
    get label() {
      return t('Visible');
    },
  },
];

export function Carousel() {
  const { focusIdx } = useFocusIdx();
  return (
    <CollapseWrapper defaultActiveKey={['0', '1', '2', '4']} style={{ padding: 0 }}>
        <Collapse.Item
          name='0'
          header={t('Dimension')}
        >
          <div className="px-2 py-3">
            <InputWithUnitField
              label={t('Thumbnail width')}
              name={`${focusIdx}.attributes.tb-width`}
              quickchange
              inline
            />
            <RadioGroupField
              label={t('Thumbnails')}
              name={`${focusIdx}.attributes.thumbnails`}
              options={options}
              inline
            />
            <Align inline />
          </div>
        </Collapse.Item>
        <Collapse.Item
          name='4'
          contentStyle={{ padding: 0 }}
          header={t('Images')}
        >
          <EditTabField
            tabPosition='top'
            name={`${focusIdx}.data.value.images`}
            label=''
            labelHidden
            renderItem={(item, index) => (
              <CarouselImage
                item={item}
                index={index}
              />
            )}
            additionItem={{
              src: 'https://www.mailjet.com/wp-content/uploads/2016/11/ecommerce-guide.jpg',
              target: '_blank',
            }}
          />
        </Collapse.Item>
        <Collapse.Item
          name='3'
          header={t('Icon')}
        >
          <div className="px-2 py-3">
            <TextField
              label={t('Left icon')}
              name={`${focusIdx}.attributes.left-icon`}
            />
            <TextField
              label={t('Right icon')}
              name={`${focusIdx}.attributes.right-icon`}
            />
            <InputWithUnitField
              label={t('Icon width')}
              name={`${focusIdx}.attributes.icon-width`}
            />
          </div>
        </Collapse.Item>

        <Collapse.Item
          name='1'
          header={t('Border')}
        >
          <div className="px-2 py-3">
            <ColorPickerField
              label={t('Hovered border')}
              name={`${focusIdx}.attributes.tb-hover-border-color`}
            />
            <ColorPickerField
              label={t('Selected Border')}
              name={`${focusIdx}.attributes.tb-selected-border-color`}
            />
            <TextField
              label={t('Border of the thumbnails')}
              name={`${focusIdx}.attributes.tb-border`}
            />
            <TextField
              label={t('Border radius of the thumbnails')}
              name={`${focusIdx}.attributes.tb-border-radius`}
            />
          </div>
        </Collapse.Item>
        <Collapse.Item
          name='4'
          header={t('Extra')}
        >
          <div className="px-2 py-3">
            <ClassName />
          </div>
        </Collapse.Item>
      </CollapseWrapper>
  );
}

function CarouselImage({
  item,
  index,
}: {
  item: ICarousel['data']['value']['images'];
  index: number;
}) {
  const { focusIdx } = useFocusIdx();
  const { onUploadImage } = useEditorProps();
  return (
    <div className="px-2 py-3">
      <ImageUploaderField
        label={t('Image')}
        labelHidden
        name={`${focusIdx}.data.value.images.[${index}].src`}
        helpText={t(
          'The image suffix should be .jpg, jpeg, png, gif, etc. Otherwise, the picture may not be displayed normally.',
        )}
        uploadHandler={onUploadImage}
      />
      <TextField
        prefix={<IconLink />}
        label={t('Url')}
        name={`${focusIdx}.data.value.images.[${index}].href`}
      />
      <SelectField
        label={t('Target')}
        name={`${focusIdx}.data.value.images.[${index}].target`}
        options={[
          {
            value: '',
            label: t('_self'),
          },
          {
            value: '_blank',
            label: t('_blank'),
          },
        ]}
      />
      <TextField
        label={t('Title')}
        name={`${focusIdx}.data.value.image.[${index}].title`}
      />
    </div>
  );
}
